// src/Components/Cadastro/ImportPrices/FormImportPrices.tsx
import React, { useState, useRef } from 'react';
import { Col, Row, Form, Badge, Button } from 'react-bootstrap';
import { 
  FaUpload, 
  FaCheckCircle, 
  FaTimes, 
  FaImage, 
  FaFilePdf, 
  FaFileImport, 
  FaExclamationCircle,
  FaSync,
  FaPlus,
  FaEdit,
  FaSave,
  FaArrowRight,
  FaInfoCircle
} from 'react-icons/fa';
import Layout from '../../data/Layout';
import api from '../../../Services/api';
import { useAppSelector } from '../../../redux/store';
import { notify, notifyError } from '../../notify/notify-component';
import { IFormImportPrices } from '../../../Pages/Cadastro/CadImportPrices';
import * as S from './styled';

// Interfaces
interface ExistingProduct {
  id: number;
  nome_banco: string;
  nome_cardapio: string;
  preco_atual: string;
  preco_cardapio: string;
  diferenca: string;
  percentual_mudanca: number;
  precisa_atualizar: boolean;
}

interface NewProduct {
  nome_cardapio: string;
  preco_sugerido: string;
}

interface AnalysisResponse {
  produtos_existentes: ExistingProduct[];
  produtos_novos: NewProduct[];
  resumo: {
    total_extraidos: number;
    produtos_encontrados: number;
    produtos_novos: number;
    precisam_atualizacao: number;
  };
}

interface UpdatedProduct {
  id: number;
  nome: string;
  preco_antigo: string;
  preco_novo: string;
}

interface CreatedProduct {
  id: number;
  nome: string;
  preco: string;
}

type ImportType = 'image' | 'pdf';
type Step = 'upload' | 'analysis' | 'processing';

export default function FormImportPrices({
  title,
  OnAddNewImport,
}: IFormImportPrices) {
  const storeMe = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);
  
  // Estados principais
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [importType, setImportType] = useState<ImportType>('image');
  const [currentStep, setCurrentStep] = useState<Step>('upload');
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

    // Estados de progresso de upload
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  
  // Estados de análise
  const [analysisResult, setAnalysisResult] = useState<AnalysisResponse | null>(null);
  const [selectedExisting, setSelectedExisting] = useState<Set<number>>(new Set());
  const [selectedNew, setSelectedNew] = useState<Set<string>>(new Set());
  
  // Estados de resultado
  const [updatedProducts, setUpdatedProducts] = useState<UpdatedProduct[]>([]);
  const [createdProducts, setCreatedProducts] = useState<CreatedProduct[]>([]);
  const [processErrors, setProcessErrors] = useState<string[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isImageFile = (file: File) => {
    return file.type.startsWith('image/') || 
           file.name.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/);
  };

  const isPdfFile = (file: File) => {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  };

  const handleFileSelect = (file: File) => {
    if (importType === 'image' && isImageFile(file)) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else if (importType === 'pdf' && isPdfFile(file)) {
      setSelectedFile(file);
      setPreviewUrl(null);
    } else {
      const expectedFormat = importType === 'image' ? 'imagem (JPG, PNG, JPEG)' : 'PDF';
      notifyError(`Por favor, selecione um arquivo ${expectedFormat}`);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  
  // Analisar arquivo - SEM HELPER
  const handleAnalyze = async () => {
    if (!selectedFile) {
      notifyError('Por favor, selecione um arquivo primeiro');
      return;
    }

    if (!storeMe?.storeActive?.id) {
      notifyError('Loja não identificada');
      return;
    }

    // Validar tamanho do arquivo antes do upload
    const maxFileSize = 50 * 1024 * 1024; // 50MB
    if (selectedFile.size > maxFileSize) {
      notifyError('Arquivo muito grande. Tamanho máximo: 50MB');
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append(importType === 'image' ? 'image' : 'pdf', selectedFile);
    formData.append('store_id', storeMe.storeActive.id.toString());

    const endpoint = importType === 'image' 
      ? '/api/v1/products/analyze-prices-from-image'
      : '/api/v1/products/analyze-prices-from-pdf';

    try {
      // Implementação direta sem helper - timeout de 5 minutos
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutos (300000ms)
        maxContentLength: 50 * 1024 * 1024,
        maxBodyLength: 50 * 1024 * 1024,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        },
      });

      const result: AnalysisResponse = response.data;
      setAnalysisResult(result);
      setCurrentStep('analysis');
      notify('Análise concluída com sucesso!');
      
    } catch (error: any) {
      console.error('Erro completo:', error);
      console.error('Erro response:', error.response);
      console.error('Erro request:', error.request);
      console.error('Erro config:', error.config);
      
      let errorMessage = 'Erro desconhecido ao processar arquivo';
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Tempo limite excedido (5 minutos). O arquivo pode ser muito complexo.';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Erro de rede. Verifique: 1) CORS no backend, 2) Gateway timeout no servidor, 3) Conexão de internet.';
      } else if (error.response?.status === 504) {
        errorMessage = 'Gateway Timeout (504). O servidor levou muito tempo para processar. Contate o suporte técnico para aumentar o timeout do gateway.';
      } else if (error.response) {
        errorMessage = error.response.data?.message || 
          `Erro ${error.response.status}: ${error.response.statusText}`;
      } else if (error.request) {
        errorMessage = 'Sem resposta do servidor. Possíveis causas: CORS, timeout do gateway, ou servidor offline.';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      notifyError('Erro na análise: ' + errorMessage);
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  // Atualizar preços selecionados
  const handleUpdatePrices = async () => {
    if (!analysisResult || selectedExisting.size === 0) return;

    setIsLoading(true);
    const productsToUpdate = Array.from(selectedExisting).map(productId => {
      const product = analysisResult.produtos_existentes.find(p => p.id === productId);
      return {
        product_id: productId,
        new_price: parseFloat(product!.preco_cardapio)
      };
    });

    try {
      const response = await api.patch('/api/v1/products/update-multiple-prices', {
        products: productsToUpdate,
        store_id: storeMe?.storeActive?.id
      });

      const result = response.data;
      setUpdatedProducts(result.atualizados || []);
      notify(`${result.atualizados?.length || 0} produtos atualizados com sucesso!`);
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      notifyError('Erro ao atualizar preços: ' + errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Criar produtos selecionados
  const handleCreateProducts = async () => {
    if (!analysisResult || selectedNew.size === 0) return;

    setIsLoading(true);
    const productsToCreate = Array.from(selectedNew).map(productName => {
      const product = analysisResult.produtos_novos.find(p => p.nome_cardapio === productName);
      return {
        sale_name: productName,
        sale_price: parseFloat(product!.preco_sugerido)
      };
    });

    try {
      const response = await api.post('/api/v1/products/create-multiple-products', {
        products: productsToCreate,
        store_id: storeMe?.storeActive?.id
      });

      const result = response.data;
      setCreatedProducts(result.criados || []);
      setProcessErrors(result.errors || []);
      notify(`${result.criados?.length || 0} produtos criados com sucesso!`);
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      notifyError('Erro ao criar produtos: ' + errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Executar todas as ações selecionadas
  const handleExecuteAll = async () => {
    setCurrentStep('processing');
    setIsLoading(true);

    try {
      // Primeiro atualizar preços
      if (selectedExisting.size > 0) {
        await handleUpdatePrices();
      }

      // Depois criar produtos
      if (selectedNew.size > 0) {
        await handleCreateProducts();
      }

      notify('Processamento concluído!');
      
    } catch (error) {
      notifyError('Erro durante o processamento');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    OnAddNewImport(false);
    resetForm();
  };

  const resetForm = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setCurrentStep('upload');
    setAnalysisResult(null);
    setSelectedExisting(new Set());
    setSelectedNew(new Set());
    setUpdatedProducts([]);
    setCreatedProducts([]);
    setProcessErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBackToUpload = () => {
    setCurrentStep('upload');
    setAnalysisResult(null);
    setSelectedExisting(new Set());
    setSelectedNew(new Set());
  };

  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const renderUploadStep = () => (
    <>
      {/* Seleção do Tipo de Importação */}
      <S.Wrapper>
        <div className="containerFilter" style={{ margin: '0 0 1rem 0' }}>
          <h3 style={{ marginBottom: '15px' }}>Tipo de Importação</h3>
          <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
            <button
              type="button"
              className={`btn ${importType === 'image' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => {
                setImportType('image');
                resetForm();
              }}
              style={{
                backgroundColor: importType === 'image' ? '#e83e8c' : 'transparent',
                borderColor: importType === 'image' ? '#e83e8c' : '#6c757d',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px'
              }}
            >
              <FaImage />
              Por Imagem
            </button>
            <button
              type="button"
              className={`btn ${importType === 'pdf' ? 'btn-danger' : 'btn-outline-secondary'}`}
              onClick={() => {
                setImportType('pdf');
                resetForm();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px'
              }}
            >
              <FaFilePdf />
              Por PDF
            </button>
          </div>
        </div>
      </S.Wrapper>

      {/* Upload de Arquivo */}
      <S.Wrapper>
        <div className="containerTable" style={{ margin: '0' }}>
          <h3>Selecionar Arquivo</h3>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{
              border: `2px dashed ${isDragOver ? '#e83e8c' : selectedFile ? '#28a745' : '#dee2e6'}`,
              borderRadius: '8px',
              padding: '40px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              minHeight: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              backgroundColor: isDragOver ? (importType === 'image' ? '#fce4ec' : '#f8d7da') : selectedFile ? '#d4edda' : 'transparent',
              transition: 'all 0.15s'
            }}
            onClick={() => !selectedFile && fileInputRef.current?.click()}
          >
            {selectedFile ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                <FaCheckCircle size={48} style={{ color: '#28a745' }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: '500', marginBottom: '5px' }}>{selectedFile.name}</div>
                  <div style={{ fontSize: '14px', color: '#6c757d' }}>
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
                {previewUrl && importType === 'image' && (
                  <div style={{ 
                    maxWidth: '200px', 
                    maxHeight: '150px', 
                    overflow: 'hidden', 
                    borderRadius: '8px',
                    border: '2px solid #dee2e6'
                  }}>
                    <img 
                      src={previewUrl} 
                      alt="Preview" 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover' 
                      }}
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetForm();
                  }}
                  className="btn btn-outline-secondary btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <FaTimes />
                  Remover arquivo
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                <div style={{ color: '#6c757d' }}>
                  {importType === 'image' ? <FaImage size={48} /> : <FaFilePdf size={48} />}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '5px' }}>
                    {importType === 'image' ? 'Arraste e solte sua imagem aqui' : 'Arraste e solte seu PDF aqui'}
                  </div>
                  <div style={{ color: '#6c757d' }}>ou clique para selecionar</div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ backgroundColor: importType === 'image' ? '#e83e8c' : '#dc3545', borderColor: importType === 'image' ? '#e83e8c' : '#dc3545' }}
                >
                  {importType === 'image' ? 'Selecionar Imagem' : 'Selecionar PDF'}
                </button>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={importType === 'image' ? '.jpg,.jpeg,.png,image/*' : '.pdf,application/pdf'}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          
          {selectedFile && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                type="button"
                onClick={handleAnalyze}
                className="btn btn-success btn-lg"
                disabled={isLoading}
                style={{ 
                  minWidth: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  margin: '0 auto'
                }}
              >
                {isLoading ? (
                  <>
                    <div 
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}
                    />
                    Analisando...
                  </>
                ) : (
                  <>
                    <FaFileImport />
                    Analisar Documento
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </S.Wrapper>
    </>
  );

  const renderAnalysisStep = () => {
    if (!analysisResult) return null;

    return (
      <>
        {/* Resumo da Análise */}
        <S.Wrapper>
          <div className="containerFilter" style={{ margin: '0 0 1rem 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>Resultados da Análise</h3>
              <button
                type="button"
                onClick={handleBackToUpload}
                className="btn btn-outline-secondary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                <FaTimes />
                Voltar
              </button>
            </div>
            
            <Row>
              <Col md={3}>
                <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
                    {analysisResult.resumo.total_extraidos}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Total Extraídos</div>
                </div>
              </Col>
              <Col md={3}>
                <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#f3e5f5', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7b1fa2' }}>
                    {analysisResult.resumo.produtos_encontrados}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Produtos Encontrados</div>
                </div>
              </Col>
              <Col md={3}>
                <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#fff3e0', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>
                    {analysisResult.resumo.precisam_atualizacao}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Precisam Atualização</div>
                </div>
              </Col>
              <Col md={3}>
                <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#e8f5e8', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#388e3c' }}>
                    {analysisResult.resumo.produtos_novos}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Produtos Novos</div>
                </div>
              </Col>
            </Row>
          </div>
        </S.Wrapper>

        {/* Produtos Existentes */}
        {analysisResult.produtos_existentes.length > 0 && (
          <S.Wrapper>
            <div className="containerTable" style={{ margin: '0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                <h3 style={{ margin: 0 }}>Produtos Existentes ({analysisResult.produtos_existentes.length})</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    {selectedExisting.size} selecionados
                  </span>
                  {selectedExisting.size > 0 && (
                    <button
                      type="button"
                      onClick={handleUpdatePrices}
                      className="btn btn-primary btn-sm"
                      disabled={isLoading}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '5px',
                        backgroundColor: '#e83e8c',
                        borderColor: '#e83e8c'
                      }}
                    >
                      <FaSync />
                      Atualizar Selecionados
                    </button>
                  )}
                </div>
              </div>

              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table table-hover" style={{ fontSize: '14px' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <Form.Check
                          type="checkbox"
                          checked={selectedExisting.size === analysisResult.produtos_existentes.filter(p => p.precisa_atualizar).length && analysisResult.produtos_existentes.some(p => p.precisa_atualizar)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const needsUpdate = analysisResult.produtos_existentes
                                .filter(p => p.precisa_atualizar)
                                .map(p => p.id);
                              setSelectedExisting(new Set(needsUpdate));
                            } else {
                              setSelectedExisting(new Set());
                            }
                          }}
                        />
                      </th>
                      <th>Produto</th>
                      <th style={{ textAlign: 'center' }}>Preço Atual</th>
                      <th style={{ textAlign: 'center' }}>Preço Cardápio</th>
                      <th style={{ textAlign: 'center' }}>Diferença</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysisResult.produtos_existentes.map((produto) => (
                      <tr 
                        key={produto.id}
                        style={{ 
                          backgroundColor: produto.precisa_atualizar ? '#fff3cd' : 'transparent',
                          opacity: produto.precisa_atualizar ? 1 : 0.7
                        }}
                      >
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedExisting.has(produto.id)}
                            disabled={!produto.precisa_atualizar}
                            onChange={(e) => {
                              const newSelected = new Set(selectedExisting);
                              if (e.target.checked) {
                                newSelected.add(produto.id);
                              } else {
                                newSelected.delete(produto.id);
                              }
                              setSelectedExisting(newSelected);
                            }}
                          />
                        </td>
                        <td>
                          <div style={{ fontWeight: '500' }}>{produto.nome_banco}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{produto.nome_cardapio}</div>
                        </td>
                        <td style={{ textAlign: 'center' }}>R$ {produto.preco_atual}</td>
                        <td style={{ textAlign: 'center', fontWeight: '600' }}>R$ {produto.preco_cardapio}</td>
                        <td style={{ 
                          textAlign: 'center', 
                          color: produto.percentual_mudanca > 0 ? '#dc3545' : produto.percentual_mudanca < 0 ? '#28a745' : '#6c757d',
                          fontWeight: '500'
                        }}>
                          {produto.percentual_mudanca > 0 ? '+' : ''}{produto.percentual_mudanca.toFixed(1)}%
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {produto.precisa_atualizar ? (
                            <Badge bg="warning" text="dark">Precisa Atualizar</Badge>
                          ) : (
                            <Badge bg="success">Atual</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </S.Wrapper>
        )}

        {/* Produtos Novos */}
        {analysisResult.produtos_novos.length > 0 && (
          <S.Wrapper>
            <div className="containerTable" style={{ margin: '0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                <h3 style={{ margin: 0 }}>Produtos Novos ({analysisResult.produtos_novos.length})</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    {selectedNew.size} selecionados
                  </span>
                  {selectedNew.size > 0 && (
                    <button
                      type="button"
                      onClick={handleCreateProducts}
                      className="btn btn-success btn-sm"
                      disabled={isLoading}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                      <FaPlus />
                      Criar Selecionados
                    </button>
                  )}
                </div>
              </div>

              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <table className="table table-hover" style={{ fontSize: '14px' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <Form.Check
                          type="checkbox"
                          checked={selectedNew.size === analysisResult.produtos_novos.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedNew(new Set(analysisResult.produtos_novos.map(p => p.nome_cardapio)));
                            } else {
                              setSelectedNew(new Set());
                            }
                          }}
                        />
                      </th>
                      <th>Nome do Produto</th>
                      <th style={{ textAlign: 'center' }}>Preço Sugerido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysisResult.produtos_novos.map((produto, index) => (
                      <tr key={index}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedNew.has(produto.nome_cardapio)}
                            onChange={(e) => {
                              const newSelected = new Set(selectedNew);
                              if (e.target.checked) {
                                newSelected.add(produto.nome_cardapio);
                              } else {
                                newSelected.delete(produto.nome_cardapio);
                              }
                              setSelectedNew(newSelected);
                            }}
                          />
                        </td>
                        <td style={{ fontWeight: '500' }}>{produto.nome_cardapio}</td>
                        <td style={{ textAlign: 'center', fontWeight: '600' }}>R$ {produto.preco_sugerido}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </S.Wrapper>
        )}

        {/* Botões de Ação */}
        {(selectedExisting.size > 0 || selectedNew.size > 0) && (
          <S.Wrapper>
            <div className="containerFilter" style={{ margin: '0', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleExecuteAll}
                className="btn btn-primary btn-lg"
                disabled={isLoading}
                style={{ 
                  minWidth: '250px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  margin: '0 auto',
                  backgroundColor: '#e83e8c',
                  borderColor: '#e83e8c'
                }}
              >
                {isLoading ? (
                  <>
                    <div 
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}
                    />
                    Processando...
                  </>
                ) : (
                  <>
                    <FaArrowRight />
                    Executar Todas as Ações
                    <span style={{ fontSize: '12px', opacity: 0.8 }}>
                      ({selectedExisting.size + selectedNew.size} itens)
                    </span>
                  </>
                )}
              </button>
            </div>
          </S.Wrapper>
        )}
      </>
    );
  };

  const renderProcessingStep = () => (
    <S.Wrapper>
      <div className="containerFilter" style={{ margin: '0' }}>
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <FaCheckCircle size={64} style={{ color: '#28a745', marginBottom: '20px' }} />
          <h3 style={{ color: '#28a745', marginBottom: '20px' }}>Processamento Concluído!</h3>
          
          {/* Resultados de Atualização */}
          {updatedProducts.length > 0 && (
            <div style={{ marginBottom: '25px' }}>
              <h4 style={{ fontSize: '18px', marginBottom: '15px', color: '#e83e8c' }}>
                Produtos Atualizados ({updatedProducts.length})
              </h4>
              <div style={{ 
                maxHeight: '200px', 
                overflowY: 'auto',
                backgroundColor: '#d4edda',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #c3e6cb'
              }}>
                <table style={{ width: '100%', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Produto</th>
                      <th style={{ textAlign: 'center' }}>Preço Anterior</th>
                      <th style={{ textAlign: 'center' }}>Novo Preço</th>
                    </tr>
                  </thead>
                  <tbody>
                    {updatedProducts.map((produto, index) => (
                      <tr key={index}>
                        <td style={{ fontWeight: '500' }}>{produto.nome}</td>
                        <td style={{ textAlign: 'center' }}>R$ {produto.preco_antigo}</td>
                        <td style={{ textAlign: 'center', fontWeight: '600', color: '#28a745' }}>
                          R$ {produto.preco_novo}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Resultados de Criação */}
          {createdProducts.length > 0 && (
            <div style={{ marginBottom: '25px' }}>
              <h4 style={{ fontSize: '18px', marginBottom: '15px', color: '#28a745' }}>
                Produtos Criados ({createdProducts.length})
              </h4>
              <div style={{ 
                maxHeight: '200px', 
                overflowY: 'auto',
                backgroundColor: '#d1ecf1',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #bee5eb'
              }}>
                <table style={{ width: '100%', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Produto</th>
                      <th style={{ textAlign: 'center' }}>Preço</th>
                      <th style={{ textAlign: 'center' }}>ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {createdProducts.map((produto, index) => (
                      <tr key={index}>
                        <td style={{ fontWeight: '500' }}>{produto.nome}</td>
                        <td style={{ textAlign: 'center', fontWeight: '600' }}>R$ {produto.preco}</td>
                        <td style={{ textAlign: 'center', fontSize: '11px', color: '#666' }}>#{produto.id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Erros */}
          {processErrors.length > 0 && (
            <div style={{ marginBottom: '25px' }}>
              <h4 style={{ fontSize: '16px', marginBottom: '10px', color: '#dc3545' }}>
                Erros Encontrados ({processErrors.length})
              </h4>
              <div style={{ 
                backgroundColor: '#f8d7da',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #f5c6cb',
                fontSize: '13px'
              }}>
                {processErrors.map((error, index) => (
                  <div key={index} style={{ marginBottom: '5px' }}>• {error}</div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={resetForm}
              className="btn btn-outline-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <FaSync />
              Nova Importação
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-secondary"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </S.Wrapper>
  );

  return (
    <>
      {/* Loading Overlay */}
      {isLoading && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '15px',
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            maxWidth: '400px',
          }}>
            <div 
              style={{
                width: '60px',
                height: '60px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #e83e8c',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }}
            />
            <h3 style={{ 
              color: '#e83e8c', 
              marginBottom: '15px',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              {currentStep === 'upload' ? `Analisando ${importType === 'image' ? 'Imagem' : 'PDF'}` : 'Processando'}
            </h3>
            <p style={{ 
              color: '#666', 
              marginBottom: '15px',
              fontSize: '14px',
            }}>
              {currentStep === 'upload' 
                ? 'Identificando produtos e preços...' 
                : 'Atualizando preços e criando produtos...'}
            </p>
          </div>
        </div>
      )}

      <Layout
        title={title}
        isEditable={currentStep !== 'processing'}
        onClickSave={currentStep === 'upload' ? handleAnalyze : handleExecuteAll}
        onClickCancel={handleCancel}
        loading={isLoading}
      >
        {/* CSS Animation */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>

        {currentStep === 'upload' && renderUploadStep()}
        {currentStep === 'analysis' && renderAnalysisStep()}
        {currentStep === 'processing' && renderProcessingStep()}
      </Layout>
    </>
  );
}